---
title: Making figma plugins
description: Lessons learned from building plugins that extend Figma's capabilities and improve design workflows.
---

It's really easy to make plugins for Figma and they can speed up pretty much any workflow.

There are three parts to this page:

1. [Manipulating the document](#manipulating-the-document) 
2. [How to make a Figma plugin](#how-to-make-a-figma-plugin)
3. [Some real-world examples I've used](#real-world-examples) 

## Manipulating the document

Figma has a fantastic [API](https://developers.figma.com/docs/plugins/api/figma/). *Anything you can see or do inside of Figma can most likely be done using a plugin.*

For some examples, open **Plugins → Development → Open Console**. This gives you access to Figma's API, just like a plugin would. Changes made here persist in the document just like any other edit.

Try out some of the following: 

### Getting information about a node

Select something on the canvas, then run this in the console:

```javascript
figma.currentPage.selection[0]
```

Expand it to explore all the properties — `name`, `type`, `width`, `height`, `x`, `y`, `fills`, etc.

### Selecting nodes

```javascript
// Select all rectangles
figma.currentPage.selection = figma.currentPage.findAll(n => n.type === "RECTANGLE")

// Select by name
figma.currentPage.selection = [figma.currentPage.findOne(n => n.name === "Button")]
```

### Creating a rectangle

```javascript
const rect = figma.createRectangle()
rect.resize(200, 100)
rect.fills = [{ type: "SOLID", color: { r: 0.2, g: 0.5, b: 1 } }]
figma.currentPage.selection = [rect]
```

## How to make a Figma plugin 

Now you just need the boilerplate to turn this into a plugin. Luckily a Figma plugin needs just two files:

1. **`manifest.json`** for plugin metadata and entry points

### Minimal manifest.json

```json
// manifest.json
{
  "name": "My Plugin",
  "id": "1234567890",
  "api": "1.0.0",
  "main": "code.js",
  "ui": "ui.html",
  "editorType": ["figma"]
}
```

2. **`code.js`** for the main plugin code 

For plugins with UI, you'll also need:

3. **`ui.html`**: This must include all of your HTML and CSS. Everything must be inlined as external scripts, stylesheets and assets are not allowed. 

Plugin code and UI code run in isolated sandboxes. They communicate via message passing like: 

```html
<!-- ui.html -->
<button onclick="parent.postMessage({ pluginMessage: 'hello' }, '*')">
  Say Hello
</button>
```

```ts
// code.ts
figma.ui.onmessage = (msg) => {
  console.log(msg) // "hello"
}
```

Note that only the UI can make fetch requests and only the code file can use Figma's plugin API. 

### Developing a plugin 

1. **Plugins → Development → Import plugin from manifest** — Point to your `manifest.json`
2. Edit your files, then **Plugins → Development → [Your Plugin]** to reload
3. Use **Plugins → Development → Open Console** to debug

### Building for production

Since the UI must be a single file,  a bundler (Vite, esbuild, webpack) should be used to compile typescript, bundle dependencies, and inline all CSS and JS into the HTML. 

If you're looking for a template to do all this for you (hell yeah), check out [create-figma-plugin](https://yuanqing.github.io/create-figma-plugin/). 

## Real-world examples

Here are just some of the things I've used the plugin api for: 
- Connecting real-world API data to Figma mock-ups [learn more](/work/plex#figma-plugins)
- Looking for out of date style references 
- Duplicating a mobile screen and converting it to desktop 
- Consistently aligning component examples on the canvas 

And of course, there's the incredible Prototyper plugin that I mention in [Designing for TVs](/thoughts/designing-for-tvs). 

Changing component properties 

The plugin API can also be really helpful for troubleshooting, such as retrieving component keys, comparing two nodes to see why content isn't persisting, and looking for external style references. 

## Resources

- [Plugin API Overview](https://www.figma.com/plugin-docs/) 
- [Getting Started Guide](https://www.figma.com/plugin-docs/plugin-quickstart-guide/)
- [API Reference](https://www.figma.com/plugin-docs/api/api-reference/)
